import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import CardSlider from 'components/consumer/CardSlider';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import CardCarousel from 'components/consumer/image-slider-standard/components/CardCarousel';
import useProductListingPageContext from 'data-hooks/useProductListingPageContext';
import useMobileColumnCount from 'components/consumer/ProductListingPage/state/mobileColumnCount/useMobileColumnCount';
import CustomProductListItemPlaceholder from './CustomProductListItemPlaceholder';

const CustomProductListUI = ({ listStyle = 'Slider' }) => {
  const {
    filterStateKey,
    isAllDataLoading,
    loading,
    products,
    productsToPreload,
  } = useProductListingPageContext();

  const mobileColumnCount = useMobileColumnCount();

  const skeletonLoaderItems =
    products?.length > 0 && products?.length < NUMBER_OF_PRODUCTS_TO_SHOW
      ? NUMBER_OF_PRODUCTS_TO_SHOW - products.length
      : NUMBER_OF_PRODUCTS_TO_SHOW;

  const productsList = useMemo(() => {
    const allProductsToShow = [
      ...products,
      ...(productsToPreload ?? []).map(product => ({
        ...product,
        isHidden: true,
      })),
    ];

    return (
      !loading &&
      allProductsToShow?.map(
        (
          {
            campaign,
            id,
            isClearance,
            isFavorited,
            isHidden,
            label,
            moreVariantsCount,
            name,
            onDisplayInShowroomSlug,
            path,
            position,
            productFamily,
            productLabel,
            productType,
            quickship,
            secondaryImage,
            secondaryImageCropData,
            slug,
            variants,
            variantsToShow,
            variantsTotal,
          },
          index
        ) => (
          <ProductCardStatic
            campaign={campaign}
            disableImageLazyLoading={listStyle === 'Grid' && index < 12}
            enableMobileCarousel={false}
            filterStateKey={filterStateKey}
            hidden={isHidden}
            id={id}
            isFavorited={isFavorited}
            isClearance={isClearance}
            // The keys for CustomProductList products are derived from the product
            // slug and the index to prevent a duplicate key error when two or
            // more of the same product are included in the custom list.

            // It is safe to use the index because the products in a custom list are returned
            // in the same order with the same indices each time.

            // eslint-disable-next-line react/no-array-index-key
            key={`${slug}-${index}`}
            label={label}
            layout="minimal-with-attributes"
            moreVariantsCount={moreVariantsCount}
            name={name}
            onDisplayInShowroomSlug={onDisplayInShowroomSlug}
            path={path}
            position={position}
            productFamily={productFamily}
            productLabel={productLabel}
            productType={productType}
            quickship={quickship}
            secondaryImage={secondaryImage}
            secondaryImageCropData={secondaryImageCropData}
            slug={slug}
            variants={variants}
            variantsToShow={variantsToShow}
            variantsTotal={variantsTotal}
          />
        )
      )
    );
  }, [loading, products, productsToPreload, filterStateKey, listStyle]);

  if (listStyle === 'Carousel') {
    return (
      <FixedWidthContainer className="CustomProductListUI">
        <CardCarousel
          imageHeight={350}
          imageWidth={350}
          navType="Button"
          visibleSlides={4}
        >
          {productsList}
        </CardCarousel>
      </FixedWidthContainer>
    );
  }

  if (listStyle === 'Slider') {
    return (
      <div className="max-w-[1500px] mx-auto my-0">
        <CardSlider wide={false}>{productsList}</CardSlider>
      </div>
    );
  }

  if (!loading && !products?.length) {
    return (
      <FixedWidthContainer className="max-[1023px]:!p-0">
        <div className="text-lg font-bold flex flex-col items-center justify-center px-0 py-8 min-h-[30vh]">
          Looks like these deals sold out in a flash! Check back soon for more
          opportunities to save big.
        </div>
      </FixedWidthContainer>
    );
  }

  return (
    <FixedWidthContainer className="max-[1023px]:!p-0">
      <ProductCardGridUI
        columnsOnDesktop="auto"
        columnsOnMobile={mobileColumnCount}
        limitWidth={false}
      >
        {loading &&
          [...Array(skeletonLoaderItems)].map((item, index) => (
            // We allow the index to be used as a key here because placeholder
            // items are stable and do not change order.
            // eslint-disable-next-line react/no-array-index-key
            <CustomProductListItemPlaceholder key={`placeholder-${index}`} />
          ))}

        {productsList}

        {!loading &&
          isAllDataLoading &&
          [...Array(skeletonLoaderItems)].map((item, index) => (
            // We allow the index to be used as a key here because placeholder
            // items are stable and do not change order.
            // eslint-disable-next-line react/no-array-index-key
            <CustomProductListItemPlaceholder key={`placeholder-${index}`} />
          ))}
      </ProductCardGridUI>
    </FixedWidthContainer>
  );
};

CustomProductListUI.propTypes = {
  listStyle: PropTypes.oneOf(['Slider', 'Carousel', 'Grid']),
};

export default CustomProductListUI;
